import * as React from 'react';
import { ClickableCircle, HoverTextNearMouse, Text } from '@react-financial-charts/interactive/lib/components/index';
import type { ElliottImpulseSharedState, HoverTextConfig, MoreProps } from '@/types/commontypes';
import { isDefined, noop } from 'react-financial-charts';
import { getXValue } from '@react-financial-charts/core/lib/utils/ChartDataUtil';
import { saveNodeType } from 'react-financial-charts';
import { ClickableArrowHead, InteractiveStraightLine } from '.';
import { renderXABCDDottedLines } from '../XABCDDottedLines';
import { getLabelText } from '@/utils/combinedText';
import { ShapeType } from '@/types/commontypes';
import { getNewXY } from '@/utils/utils';
import { fixedAppearance } from '@/utils/defaultShapePropertyValues';

export interface EachElliottImpulseProps {
  readonly uuid: string;
  readonly type:
    | 'XLINE' // extends from -Infinity to +Infinity
    | 'RAY' // extends to +/-Infinity in one direction
    | 'LINE'; // extends between the set bounds
  /** Shape kind discriminator (RFC mode `type`와는 별개). XABCD 패턴 분기 등에 사용. */
  readonly shapeType?: ShapeType;
  readonly index: number;
  readonly selected?: boolean;
  readonly points: [number, number][];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly appearance: Record<string, any>;
  readonly onDrag: (
    e: React.MouseEvent,
    impulseUuid: string,
    idx: number,
    newXY: [number, number] | [number, number][],
  ) => void;
  readonly onDragComplete: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onSelect?: (e: React.MouseEvent, uuid: string, moreProps: MoreProps) => void;
  readonly defaultClassName?: string;
  readonly onEdge1Drag?: (...args: unknown[]) => unknown; // func
  readonly onEdge2Drag?: (...args: unknown[]) => unknown; // func
  readonly edgeInteractiveCursor: string;
  readonly lineInteractiveCursor: string;
  readonly hoverText: HoverTextConfig;
  readonly onDragStart?: (uuid: string) => void;
  readonly onHover?: (uuid: string | null, moreProps?: MoreProps) => void;
  readonly locked: boolean;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly sharedState: ElliottImpulseSharedState;
  readonly isAnyShapeEnabled?: boolean;
}

export interface EachElliottImpulseState {
  anchor?: string;
  hover?: boolean;
  dragging: boolean;
}

export class EachElliottImpulse extends React.Component<EachElliottImpulseProps, EachElliottImpulseState> {
  public static defaultProps = {
    onDrag: noop,
    onEdge1Drag: noop,
    onEdge2Drag: noop,
    onSelect: noop,
    selected: false,
    appearance: {
      strokeStyle: '#000000',
      strokeWidth: 1,
      strokeDasharray: 'Solid',
      ...fixedAppearance,
      fontFamily: 'Arial',
      fontSize: 12,
      arrowDirection: 'none',
    },
    hoverText: {
      enable: false,
    },
    locked: false,
  };
  private dragStart: [number, number][] = [];
  private lineInteractive: string;
  private saveNodeType: <T>(type: string) => React.Ref<T>;

  public constructor(props: EachElliottImpulseProps) {
    super(props);

    this.saveNodeType = saveNodeType.bind(this);
    this.lineInteractive = 'react-financial-charts-pointer-cursor';
    this.state = {
      hover: false,
      dragging: false,
    };
  }
  render() {
    const { points, appearance, uuid, selected, hoverText, type, shapeType, onDoubleClick, locked } = this.props;
    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;
    const { hover } = this.state;
    const {
      strokeDasharray,
      strokeStyle,
      strokeWidth,
      edgeStroke,
      fontFamily,
      fontSize,
      edgeStrokeWidth,
      edgeFill,
      arrowDirection,
      r,
    } = appearance;
    const lockedR = locked ? r - 2 : r;
    const lockedEdgeStorke = locked ? '#436fe9' : edgeStroke;
    const lockedStrokeWidth = locked ? 1 : edgeStrokeWidth;

    const { dragging } = this.state;
    if (dragging) {
      this.lineInteractive = 'react-financial-charts-grabbing-cursor';
    } else if (!isDefined(dragging) || !dragging) {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    } else {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const isPath = (type as unknown as string) === ShapeType.PATH;
    return (
      <g>
        {points.slice(0, -1).map((start, lineIdx) => {
          const end = points[lineIdx + 1];
          return (
            <InteractiveStraightLine
              ref={this.saveNodeType(`line-${uuid}-${lineIdx}`)}
              key={`line-${uuid}-${lineIdx}`}
              x1Value={start[0]}
              y1Value={start[1]}
              x2Value={end[0]}
              y2Value={end[1]}
              type={type}
              strokeStyle={strokeStyle}
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              interactiveCursorClass={this.lineInteractive}
              onDragStart={this.handleLineDragStart}
              onDrag={this.handleLineDrag}
              onDragComplete={this.handleDragComplete}
              selected={selected || hover}
              onHover={this.handleHover}
              onUnHover={this.handleHover}
              onMouseDown={this.handleShapeMouseDown}
              onDoubleClick={onDoubleClick}
            />
          );
        })}

        {shapeType === ShapeType.XABCD_PATTERN &&
          renderXABCDDottedLines(
            points,
            appearance.strokeStyle,
            appearance.strokeWidth,
            {
              fill: appearance.textFill!,
              fontSize: appearance.fontSize,
              fontFamily: appearance.fontFamily,
              fontWeight: appearance.fontWeight!,
              fontStyle: appearance?.fontStyle ?? 'normal',
            },
            appearance.fillStyle,
            uuid,
          )}

        {/* Arrowheads */}
        {points.length >= 2 && (
          <>
            {(arrowDirection === 'left' || arrowDirection === 'both') && (
              <ClickableArrowHead
                key={`arrow-left-${uuid}`}
                ref={this.saveNodeType(`arrow-left-${uuid}`)}
                show={true}
                startXY={points[0]} // second point for direction
                endXY={points[1]} // arrow points to start
                r={lockedR}
                fillStyle={edgeFill}
                arrowDirection="left"
                strokeStyle={strokeStyle}
                strokeWidth={strokeWidth}
                strokeDasharray={strokeDasharray}
                interactiveCursorClass={this.lineInteractive}
                onDragStart={this.handleDragStart(0)}
                onDrag={this.handleDrag(0)}
                onDragComplete={this.handleDragComplete}
              />
            )}
            {(arrowDirection === 'right' || arrowDirection === 'both') && (
              <ClickableArrowHead
                key={`arrow-right-${uuid}`}
                ref={this.saveNodeType(`arrow-right-${uuid}`)}
                show={true}
                startXY={points[points.length - 2]} // second last
                endXY={points[points.length - 1]} // arrow points to end
                r={lockedR}
                fillStyle={edgeFill}
                arrowDirection="right"
                strokeStyle={strokeStyle}
                strokeWidth={strokeWidth}
                strokeDasharray={strokeDasharray}
                interactiveCursorClass={this.lineInteractive}
                onDragStart={this.handleDragStart(points.length - 1)}
                onDrag={this.handleDrag(points.length - 1)}
                onDragComplete={this.handleDragComplete}
              />
            )}
          </>
        )}

        {points.map(([x, y], circleIdx) => (
          <g key={`group-${uuid}-${circleIdx}`}>
            <ClickableCircle
              ref={this.saveNodeType(`circle-${uuid}-${circleIdx}`)}
              // key={`circle-${id}-${circleIdx}`}
              cx={x}
              cy={y}
              r={lockedR}
              fillStyle={appearance.edgeFill}
              strokeStyle={lockedEdgeStorke}
              strokeWidth={lockedStrokeWidth}
              interactiveCursorClass="big-declined-arrow-cursor"
              show={selected || hover || locked}
              onDragStart={this.handleDragStart(circleIdx)}
              onDrag={this.handleDrag(circleIdx)}
              onDragComplete={this.handleDragComplete}
              // onDoubleClick={onDoubleClick}
            />
            {!isPath && (
              <Text
                key={`label-${uuid}-${circleIdx}`}
                fontFamily={fontFamily}
                fontSize={fontSize}
                fillStyle={strokeStyle}
                selected={selected || hover}
                xyProvider={({ xScale, chartConfig }: MoreProps) => {
                  const { yScale } = chartConfig;
                  const xPos = xScale(x);
                  const yPos = yScale(y);
                  return [xPos, yPos - 10];
                }}
              >
                {`(${getLabelText(circleIdx, type)})`}
              </Text>
            )}
          </g>
        ))}

        <HoverTextNearMouse
          show={hoverTextEnabled === true && hover === true}
          {...restHoverTextProps}
          text={selected ? hoverTextSelected : hoverTextUnselected}
        />
      </g>
    );
  }

  private readonly handleShapeMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { onSelect, uuid, sharedState } = this.props;
    if (sharedState?.status === 'started') return;
    if (onSelect && isDefined(uuid)) {
      onSelect(e, uuid, moreProps);
    }
  };

  private readonly handleHover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onHover, sharedState, isAnyShapeEnabled } = this.props;
    if (sharedState?.status === 'started' || isAnyShapeEnabled) return;
    const hovering = moreProps.hovering;
    if (this.state.hover !== hovering) {
      this.setState({
        hover: hovering,
      });
      if (hovering && onHover) {
        onHover(uuid ?? null, moreProps);
      }
    }
  };

  private readonly handleDrag = (idx: number) => (e: React.MouseEvent, moreProps: MoreProps) => {
    if (this.props.locked) return;
    const [x2Value, y2Value] = getNewXY(moreProps);
    if (moreProps?.hovering) {
      this.props.onDrag(e, this.props.uuid, idx, [x2Value, y2Value]);
    }
  };

  private readonly handleDragStart = (_: number) => () => {
    const { uuid, onDragStart, locked } = this.props;
    if (locked) return;
    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    this.setState({
      anchor: `impulse${_}edge`,
      dragging: true,
    });
  };

  private readonly handleDragComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    this.setState({
      anchor: undefined,
      dragging: false,
    });

    const { onDragComplete, locked } = this.props;
    if (onDragComplete === undefined || locked) {
      return;
    }
    if (moreProps?.hovering) {
      onDragComplete(e, moreProps);
    }
  };

  private readonly handleLineDrag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
    } = moreProps;
    const { onDrag, uuid, locked } = this.props;
    const startPos = moreProps.startPos!;
    const { mouseXY, hovering } = moreProps;
    const fullDataArr = (fullData ?? []) as unknown[];
    if (locked) return;
    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];
    const movedPoints: [number, number][] = this.dragStart.map(([x, y]) => {
      const screenX = xScale(x);
      const screenY = yScale(y);

      const newX = getXValue(xScale, xAccessor, [screenX - dx, screenY - dy], fullDataArr);
      const newY = yScale.invert(screenY - dy);

      return [newX, newY];
    });

    this.setState({ dragging: true });
    // Use -1 to indicate full shape drag
    if (hovering) {
      onDrag(e, uuid, -1, movedPoints);
    }
  };

  private readonly handleLineDragStart = () => {
    const { uuid, onDragStart, points, locked } = this.props;
    if (locked) return;
    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    this.dragStart = points.map(pt => [...pt]);
    this.setState({ anchor: 'wholeShape', dragging: true });
  };
}
